import { Link, Redirect, Tabs, RelativePathString } from 'expo-router';
import React, { useEffect } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { CustomTextStyles } from '@/components/CustomText';
import { useColorScheme } from '@/components/useColorScheme';
import Colors from '@/constants/Colors';
import { Image } from 'expo-image';
import { Button, Text } from 'react95-native-rabbl';
import { createBottomTabNavigator, BottomTabBarProps, BottomTabBarButtonProps } from "expo-router/js-tabs";
import { useLinkBuilder, useTheme } from "expo-router/react-navigation";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConsts from '@/utilities/AppConsts';
import * as AppUtilities from '@/utilities/AppUtilities';
import { ExternalLink } from '@/components/ExternalLink';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setDrawerOpen } from '@/state/user';
import { Gender } from '@/types/user_info';
import MatchesButton from '@/components/MatchesButton';
import DrawerButton from '@/components/DrawerButton';
import { setData } from '@/state/user';
import { CommunityMembership } from "@/types/community_membership";
import { HeaderLeft } from '@/components/HeaderLeft';
import { WidthResponsiveText } from '@/components/WidthResponsiveText';

const styles = StyleSheet.create({
  image28x28: {
    width: 28,
    height: 28,
  },
  headerIcon: {
    width: 28,
    height: 28,
    marginLeft: 15,
    marginRight: 10,
  },
  tabButtonsContainerStyle: {
    flexDirection: 'row',
    gap: 5,
    justifyContent: 'center',
    padding: AppConsts.TAB_BUTTON_PADDING,
  },
  tabButtonContainerStyle: {
    width: AppConsts.TAB_BUTTON_WIDTH,
  },
  badge: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: 'red',
    borderRadius: 10,
    paddingHorizontal: 5,
    color: 'white',
    fontSize: 10,
    fontWeight: 'bold',
    paddingVertical: 1,
  },
  matrixIcon: {
    width: 19,
    height: 19,
    marginVertical: 5,
    marginHorizontal: 20,
  },
  lightningIcon: {
    width: 13,
    height: 13,
    marginVertical: 10,
    marginHorizontal: 20,
  },
});

function MyTabBar({ state, descriptors, navigation }: BottomTabBarProps) {
  const { colors } = useTheme();

  return (
    <View style={styles.tabButtonsContainerStyle}>
      {state.routes.map((route, index) => {
        const { options } = descriptors[route.key];
        const label : any =
          options.tabBarLabel !== undefined
            ? options.tabBarLabel
            : options.title !== undefined
              ? options.title
              : route.name;

        const isFocused = state.index === index;

        return (
          <View
            style={styles.tabButtonContainerStyle}
            key={route.name}
          >
            {options.tabBarIcon && options?.tabBarIcon({ focused: isFocused, color: isFocused ? colors.primary : colors.text, size: 28})}
          </View>
        );
      })}
    </View>
  );
}

export default function TabLayout() {
  const colorScheme = useColorScheme();
  const community = useSelector((state: RootState) => state.user.current_community);
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const domain = useSelector((state: RootState) => state.user.domain);
  const dispatch = useDispatch();

  if (communityMembership) {
    const nextPage = AppUtils.getProfileCreateNextPageLink(communityMembership);
    if (nextPage != "/" &&

        // Pending https://github.com/expo/expo/issues/42727
        nextPage != AppConsts.SUBDOMAIN_HOMEPAGE_LOGGED_IN
       ) {

      AppUtilities.info("(community-loggedin-member-alltabs)/_layout.tsx TabLayout render detected next page: " + nextPage);
      return (
        <Redirect href={nextPage as RelativePathString} />
      );
    } else {
      AppUtilities.info("(community-loggedin-member-alltabs)/_layout.tsx TabLayout render skipping redirect");
    }
  }

  useEffect(() => {
    AppUtilities.info("(community-loggedin-member-alltabs)/_layout useEffect domain: " + domain + ", interval: " + AppConsts.REFRESH_INTERVAL_LOGGED_IN_REFRESH_MS);
    const interval = setInterval(async () => {
      try {

        if (AppUtilities.DEBUGLEVEL.dev) {
          //AppUtilities.info("(community-loggedin-member-alltabs)/_layout interval");
        }

        // NOTE: We pass the parameter to not print details about this request periodically
        const response = await AppUtilities.fetchAuthorized(
          "(community-loggedin-member-alltabs)/_layout refresh",
          "/rabbl/api/refresh",
          "POST",
          {
            domain: domain,
            // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/now
            lastChecked: Date.now() - AppConsts.REFRESH_INTERVAL_LOGGED_IN_REFRESH_MS,
          },
          null,
          false,
          false,
          true,
        );

        const membership: CommunityMembership = ((response as any).body as any).community_membership;
        if (membership) {
          if (AppUtilities.DEBUGLEVEL.dev) {
            AppUtilities.info("(community-loggedin-member-alltabs)/_layout found new updates");
          }
          dispatch(setData(response));
        }

      } catch (e) {
        if (AppUtilities.DEBUGLEVEL.extra) {
          AppUtilities.dir(e);
        }
      }

    }, AppConsts.REFRESH_INTERVAL_LOGGED_IN_REFRESH_MS);

    return () => {
      if (AppUtilities.DEBUGLEVEL.dev) {
        //AppUtilities.info("(community-loggedin-member-alltabs)/_layout cleared interval");
      }
      clearInterval(interval);
    };
  }, [domain, communityMembership]);

  return (
    <Tabs
      tabBar={(props) => <MyTabBar {...props} />}
      backBehavior='fullHistory'
      screenOptions={{

        // An animation is needed because we use transparent backgrounds on everything and
        // Tabs by default keeps everything mounted, so without the animation moving the tab
        // off screen, the tabs overlap.
        //animation: 'fade',
        //animation: 'shift',
        animation: 'fade',

        headerLeft: () => <HeaderLeft extraPadding={true} />,

        headerRight: () => <DrawerButton />,

        /*headerRight: () =>
          communityMembership && communityMembership.user_info ?
              communityMembership.user_info.gender == Gender.GENDER_MAN ?
                  (
                    <Pressable onPress={() => dispatch(setDrawerOpen(true)) }>
                      <Image
                        style={styles.matrixIcon}
                        source={require('@/assets/images/neo.png')}
                      />
                    </Pressable>
                  )
                :
                  (
                    <Pressable onPress={() => dispatch(setDrawerOpen(true)) }>
                      <Image
                        style={styles.matrixIcon}
                        source={require('@/assets/images/trinity.png')}
                      />
                    </Pressable>
                  )
            :
              (
                <Pressable onPress={() => dispatch(setDrawerOpen(true)) }>
                  <Image
                    style={styles.lightningIcon}
                    source={require('@/assets/images/lightning2.png')}
                  />
                </Pressable>
              ),*/

        tabBarActiveTintColor: Colors(colorScheme).tint,

        // Disable the static render of the header on web
        // to prevent a hydration error in React Navigation v6.
        //headerShown: useClientOnlyValue(false, true),

        headerStyle: {
          backgroundColor: 'transparent',
        },

        sceneStyle: {
          backgroundColor: 'transparent',
        },

        tabBarShowLabel: false,

        tabBarHideOnKeyboard: true,

        headerTitleStyle: CustomTextStyles.headerTitle,

        // Remove horizontal bottom line for header
        headerShadowVisible: false,

        tabBarStyle: {
          backgroundColor: 'transparent',
          
          /* Start: remove horizontal top line for tab bar */
          elevation: 0,      // For Android (removes shadow)
          borderTopWidth: 0, // For iOS
          shadowOffset: {    // For iOS (removes shadow)
            width: 0,
            height: 0,
          },
          /* End: remove horizontal top line for tab bar */
        },

      }}>
      <Tabs.Screen
        name="index"
        options={{
          title: community.name,
          headerTitle: ({ children }) => (
            <WidthResponsiveText leftPadding={50} rightPadding={50} style={CustomTextStyles.headerTitle}>
              {children}
            </WidthResponsiveText>
          ),
          tabBarIcon: ({ focused, color }) => <Link href="/" asChild={true} className={"nontext"}><Button active={focused}><Image source={require('@/assets/images/flowersbutterfly.png')} style={styles.image28x28} /></Button></Link>,
        }}
      />
      <Tabs.Screen
        name="chats"
        options={({ route, navigation }) => ({
          title: 'Chats',
          tabBarIcon: ({ focused, color }) => <MatchesButton focused={focused} />,
        })}
      />
      <Tabs.Screen
        name="myprofile"
        options={{
          title: 'Profile',
          tabBarIcon: ({ focused, color }) =>
            <Link href="/myprofile" asChild={true} className={"nontext"}>
              <Button active={focused}>
                {/*<Image source={require('@/assets/images/character28x28.png')} style={styles.image28x28} />*/}
                {
                  communityMembership && communityMembership.user_info && communityMembership.user_info.gender == Gender.GENDER_MAN ?
                      <Image style={styles.matrixIcon} source={require('@/assets/images/neo.gif')} />
                    :
                      <Image style={styles.matrixIcon} source={require('@/assets/images/trinity.gif')} />
                }
              </Button>
            </Link>,
        }}
      />
    </Tabs>
  );
}
